import { request } from '@umijs/max';
import type { ApiResponse, PageQuery, PageResult } from '@/typings.d'; // 导入全局类型

const USER_BASE_URL = "/api/v1/users";

/** 登录用户信息 */
export interface UserInfo {
  /** 用户ID */
  userId?: number;
  /** 用户名 */
  username?: string;
  /** 昵称 */
  nickname?: string;
  /** 头像URL */
  avatar?: string;
  /** 角色 */
  roles: string[];
  /** 权限 */
  perms: string[];
}

/** 用户分页查询对象 */
export interface UserPageQuery extends PageQuery {
  keywords?: string;
  status?: number;
  deptId?: number;
  startTime?: string;
  endTime?: string;
}

/** 用户分页对象 */
export interface UserPageVO {
  avatar?: string;
  createTime?: Date; // Consider string if date handling is complex, or use dayjs
  deptName?: string;
  email?: string;
  genderLabel?: string;
  id?: number;
  mobile?: string;
  nickname?: string;
  roleNames?: string;
  status?: number;
  username?: string;
}

/** 用户表单类型 */
export interface UserForm {
  avatar?: string;
  deptId?: number;
  email?: string;
  gender?: number;
  id?: number;
  mobile?: string;
  nickname?: string;
  roleIds?: number[];
  status?: number;
  username?: string;
}

/** 获取当前登录用户信息 */
export async function getUserInfo(options?: { [key: string]: any }): Promise<ApiResponse<UserInfo>> {
  return request<ApiResponse<UserInfo>>(`${USER_BASE_URL}/me`, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 获取用户分页列表 */
export async function getUserPage(params: UserPageQuery, options?: { [key: string]: any }): Promise<ApiResponse<PageResult<UserPageVO[]>>> {
  return request<ApiResponse<PageResult<UserPageVO[]>>>(`${USER_BASE_URL}/page`, {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

/** 获取用户表单详情 */
export async function getUserFormData(userId: number, options?: { [key: string]: any }): Promise<ApiResponse<UserForm>> {
  return request<ApiResponse<UserForm>>(`${USER_BASE_URL}/${userId}/form`, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 添加用户 */
export async function addUser(data: UserForm, options?: { [key: string]: any }): Promise<ApiResponse<any>> { // Assuming success returns some confirmation or the new user form
  return request<ApiResponse<any>>(`${USER_BASE_URL}/add`, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

/** 修改用户 */
export async function updateUser(id: number, data: UserForm, options?: { [key: string]: any }): Promise<ApiResponse<any>> {
  return request<ApiResponse<any>>(`${USER_BASE_URL}/${id}/edit`, { // Vue project used /${id}/edit, confirm this is desired or if just /${id} for PUT
    method: 'PUT',
    data,
    ...(options || {}),
  });
}

/** 修改用户密码 */
export async function updateUserPassword(id: number, password: string, options?: { [key: string]: any }): Promise<ApiResponse<any>> {
  return request<ApiResponse<any>>(`${USER_BASE_URL}/${id}/password`, {
    method: 'PATCH',
    params: { password }, // In Vue, password was in params, confirm this is correct for PATCH
    ...(options || {}),
  });
}

/** 批量删除用户 */
export async function deleteUsersByIds(ids: string, options?: { [key: string]: any }): Promise<ApiResponse<any>> {
  return request<ApiResponse<any>>(`${USER_BASE_URL}/${ids}/delete`, { // Vue project used /${ids}/delete, confirm this is desired or if just /${ids}
    method: 'DELETE',
    ...(options || {}),
  });
}

/** 下载用户导入模板 */
export async function downloadUserTemplate(options?: { [key: string]: any }): Promise<Blob> { // Assuming it returns a Blob for file download
  return request<Blob>(`${USER_BASE_URL}/template`, {
    method: 'GET',
    responseType: 'blob', // Changed from arraybuffer to blob, more common for direct download handling
    ...(options || {}),
  });
}

/** 导出用户 */
export async function exportUsers(params: UserPageQuery, options?: { [key: string]: any }): Promise<Blob> {
  return request<Blob>(`${USER_BASE_URL}/export`, {
    method: 'GET',
    params,
    responseType: 'blob',
    ...(options || {}),
  });
}

/** 导入用户 */
export async function importUsers(deptId: number, file: File, options?: { [key: string]: any }): Promise<ApiResponse<any>> {
  const formData = new FormData();
  formData.append("file", file);
  // In Vue, deptId was in params. For FormData POST, it's often part of the FormData itself or a query param.
  // Adding as query param for now to match original structure.
  return request<ApiResponse<any>>(`${USER_BASE_URL}/import?deptId=${deptId}`, {
    method: 'POST',
    data: formData,
    // headers: { 'Content-Type': 'multipart/form-data' } // Not needed with FormData
    ...(options || {}),
  });
}
